import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import EventList from './EventList';
import AttendanceManager from './AttendanceManager';
import CheckoutProcessor from './CheckoutProcessor';
import * as api from '../../utils/api';
import './EventManager.css';

const EventManager = ({ token }) => {
    const { eventId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();

    const [event, setEvent] = useState(null);
    const [loadingEvent, setLoadingEvent] = useState(false);


    // The URL is the source of truth for which sub-view is showing:
    //   /event-manager             -> events (list)
    //   /event-manager/:eventId    -> detail (list, with that row expanded)
    //   /event-manager/:eventId/manage   -> attendance
    //   /event-manager/:eventId/checkout -> checkout
    const view = !eventId
        ? 'events'
        : location.pathname.endsWith('/manage')
            ? 'attendance'
            : location.pathname.endsWith('/checkout')
                ? 'checkout'
                : 'detail';

    // AttendanceManager/CheckoutProcessor need the full event object, and
    // (unlike the list view) can be reached via a direct/refreshed URL, so
    // fetch it from the eventId param rather than relying on it having been
    // passed in via a click.
    useEffect(() => {
        if (view !== 'attendance' && view !== 'checkout') {
            setEvent(null);
            return;
        }
        let cancelled = false;
        (async () => {
            setLoadingEvent(true);
            try {
                const ev = await api.fetchEvent(eventId);
                if (!cancelled) setEvent(ev);
            } catch {
                if (!cancelled) setEvent(null);
            } finally {
                if (!cancelled) setLoadingEvent(false);
            }
        })();
        return () => { cancelled = true; };
    }, [view, eventId]);

    const handleSelectEvent = (ev) => {
        if (!ev) {
            navigate('/event-manager');
            return;
        }
        navigate(String(ev.eventId) === eventId ? '/event-manager' : `/event-manager/${ev.eventId}`);
    };
    const handleManageAttendance = (ev) => navigate(`/event-manager/${ev.eventId}/manage`);
    const handleProcessCheckout = (ev) => navigate(`/event-manager/${ev.eventId}/checkout`);
    const handleBackToEvents = () => navigate('/event-manager');

    return (
        <div className="em-container">
            <div className="em-header">
                <h1 className="em-title">
                    Event Manager{view === 'checkout' && <span className="em-title-crumb"> → Process Checkout</span>}
                </h1>
                {view !== 'events' && (
                    <button className="em-back-btn" onClick={handleBackToEvents}>
                        ← Back to Events
                    </button>
                )}
            </div>

            {(view === 'events' || view === 'detail') && (
                <EventList
                    token={token}
                    selectedEventId={eventId ? parseInt(eventId, 10) : null}
                    onSelectEvent={handleSelectEvent}
                    onClearSelection={handleBackToEvents}
                    onManageAttendance={handleManageAttendance}
                    onProcessCheckout={handleProcessCheckout}
                />
            )}

            {view === 'attendance' && (
                loadingEvent ? <div className="em-loading">Loading event…</div> :
                event && <AttendanceManager token={token} event={event} />
            )}

            {view === 'checkout' && (
                loadingEvent ? <div className="em-loading">Loading event…</div> :
                event && <CheckoutProcessor token={token} event={event} />
            )}

            <ToastContainer position="bottom-right" autoClose={3000} />
        </div>
    );
};

export default EventManager;
